import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';

import { AsyncScheduleInfo } from '../../../../core/interfaces/async-schedule-info.interface';
import { MachineScheduleActionEnum } from '../../../../core/enums/machine-schedule-action.enum';
import { MachineScheduleInterface } from '../../../../core/interfaces/machine-schedule.interface';
import { MonitorHub } from '../../../hubs/monitor.hub';
import { ManageSystemMessagesEnum } from '../../../../core/enums/toastr-messages/manage-system-messages.enum';

@Component({
  selector: 'app-schedule-time',
  templateUrl: './schedule-time.component.html',
  styles: [
    'button { outline: none } .schedule-checkbox { font-size: 14px } form { width: 100% }'
  ]
})
export class ScheduleTimeComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  public actions: typeof MachineScheduleActionEnum = MachineScheduleActionEnum;
  public form: UntypedFormGroup;
  public loading: boolean = true;
  public allowSchedule: boolean;
  public allowScheduleMonitors: boolean;
  public displaysOff: Date = new Date();
  public displaysOn: Date = new Date();

  constructor(
    private dialogRef: MatDialogRef<ScheduleTimeComponent>,
    private fb: UntypedFormBuilder,
    private hub: MonitorHub,
    @Inject(MAT_DIALOG_DATA) private data: { physicalAddresses: string[] },
    private readonly toastrService: ToastrService,
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      action: [this.actions.Restart],
      restartTime: [new Date()],
      shutdownTime: [new Date()],
      restartAppTime: [new Date()]
    });

    this.data.physicalAddresses.forEach((physicalAddress: string) => this.hub.invokeFetchingScheduleData(physicalAddress));

    this.subscription.add(
      this.hub.AsyncScheduleDesktopData.subscribe((schedule: AsyncScheduleInfo) => {
        this.patchFormValues(schedule);
        this.loading = false;
        this.onAllowSchedule({ checked: schedule.EnableRestart || schedule.EnableShutdown || schedule.EnableRestartApp });
        this.displaysOff = new Date(schedule.DisplaysOff);
        this.displaysOn = new Date(schedule.DisplaysOn);
        this.allowScheduleMonitors = schedule.EnableDisplaysOnAndOff;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private patchFormValues({
    Restart: restartTime,
    Shutdown: shutdownTime,
    RestartApp: restartAppTime,
    EnableShutdown,
    EnableRestart,
    PhysicalAddress
  }: AsyncScheduleInfo): void {
    let action = this.actions.Restart;
    if (EnableShutdown && !EnableRestart) {
      action = this.actions.ShutDown;
    }

    if (_.some(this.data.physicalAddresses, (physicalAddress: string) => physicalAddress === PhysicalAddress)) {
      this.form.patchValue({
        restartTime,
        shutdownTime,
        restartAppTime,
        action
      });
    }
  }

  onAllowSchedule({ checked }: { checked: boolean }): void {
    checked ? this.form.enable() : this.form.disable();
    this.allowSchedule = checked;
  }

  getFormValue(key: string) {
    return this.form.get(key).value;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  send(): void {
    if (this.allowScheduleMonitors ? +this.displaysOn !== +this.displaysOff : true) {
      const actionFieldMap: Record<MachineScheduleActionEnum, string> = {
        [MachineScheduleActionEnum.Restart]: 'restartTime',
        [MachineScheduleActionEnum.RestartApp]: 'restartAppTime',
        [MachineScheduleActionEnum.ShutDown]: 'shutdownTime',
      };

      const schedule = new MachineScheduleInterface(
        new Date(this.getFormValue(actionFieldMap[this.getFormValue('action')])).getHours(),
        new Date(this.getFormValue(actionFieldMap[this.getFormValue('action')])).getMinutes(),
        new Date(this.displaysOff).getHours(),
        new Date(this.displaysOff).getMinutes(),
        new Date(this.displaysOn).getHours(),
        new Date(this.displaysOn).getMinutes(),
        this.data.physicalAddresses,
        this.allowSchedule && this.getFormValue('action') === this.actions.Restart,
        this.allowSchedule && this.getFormValue('action') === this.actions.ShutDown,
        this.allowSchedule && this.getFormValue('action') === this.actions.RestartApp,
        this.allowScheduleMonitors
      );

      this.dialogRef.close(schedule);
    } else {
      this.toastrService.error(ManageSystemMessagesEnum.TimeToOffDisplaysEqualToOn);
    }
  }
}
